import React, { useEffect } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "../../design/design-system";
import { groupPrescriptionsByHospital } from "utils/PrescriptionUtils";
import { Drug, Prescription, PrescriptionHospitalGroup, PrescriptionSource } from "common/models/types";
import { log, LogLevel } from "utils/LogUtil";
import Image from "../../atoms/Image";
import Icon from "../../atoms/Icon";
import InfoIconButton from "../../atoms/InfoIconButton";
import { getDrugImageUrl } from "../../../utils/drugImageUtils";
import { isOngoingOn } from "../../../utils/prescriptionPrintScope";
import { meftDivName } from "../../../common/constants/meftDivCategories";
import { atcMainName } from "../../../common/constants/atcCategories";
import { SOURCE_ICON } from "common/constants/prescriptionSource";

const SourceLegend: React.FC = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px" }}>
    {Object.entries(SOURCE_ICON).map(([key, { name, label }]) => (
      <div key={key} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Icon name={name} size={13} color={COLOR_SYSTEM.get("Gray")?.[300]} />
        <span>{label}</span>
      </div>
    ))}
  </div>
);

const SourceBadge: React.FC<{ source?: PrescriptionSource | "" }> = ({ source }) => {
  if (!source || !(source in SOURCE_ICON)) return null;
  const { name, label } = SOURCE_ICON[source as PrescriptionSource];
  return (
    <span title={label} style={{ marginLeft: 4, display: "inline-flex", verticalAlign: "middle" }}>
      <Icon name={name} size={13} color={COLOR_SYSTEM.get("Gray")?.[400]} />
    </span>
  );
};

const PrescriptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// 처방의약품 제목 아래 범위 설명문 (인쇄 뷰)
const PrescriptionCaption = styled.p`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  margin: 0 0 10px 0;
`;

/* 분류 제목(처방의약품/일반의약품/건강기능식품) — 하위 "처방전 N"(14px)·본문(13px)과
   확실히 구분되는 크기. 섹션 제목(인쇄 뷰 18px) 바로 아래 층위 */
const TableTitle = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
`;

const HospitalGroupContainer = styled.div`
  margin-bottom: 24px;
`;

const HospitalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  background: ${COLOR_SYSTEM.get("Blue")?.[50]};
  border-radius: 8px;
  border-left: 4px solid ${COLOR_SYSTEM.get("Blue")?.[400]};
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Blue")?.[600]};

  @media print {
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
`;

const PrescriptionTableWrap = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  /* 좁은 폭에서 표가 잘리지 않고 가로 스크롤되게 한다(계획 item 9). 세로는 hidden 유지 →
     border-radius 코너 클리핑도 그대로다. 공유 컴포넌트라 약사 print·3단 좌측 패널에도 적용된다
     (AC-64·65·66). 인쇄 시엔 상위 셸의 overflow:visible 리셋이 이겨 전체 표가 인쇄된다(AC-50). */
  overflow-x: auto;
  overflow-y: hidden;
  margin-bottom: 16px;
`;

const PrescriptionTable = styled.table<{ $clickable?: boolean }>`
  width: 100%;
  min-width: 350px;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
  th,
  td {
    padding: 8px 4px;
    text-align: left;
    border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  }
  th {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
    color: ${COLOR_SYSTEM.get("Gray")?.[500]};
    font-weight: 500;
    font-size: 12px;

    @media print {
      print-color-adjust: exact !important;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
  }
  ${(props) =>
    props.$clickable &&
    `
    tbody tr:hover {
      background: ${COLOR_SYSTEM.get("Gray")?.[50]};
    }
  `}
  td {
    color: ${COLOR_SYSTEM.get("Gray")?.[800]};
    font-size: 13px;
    word-break: break-word;
    overflow-wrap: break-word;
    white-space: normal;
  }

  /* 하이라이트된 행 스타일 */
  tbody tr.highlighted {
    background-color: rgba(139, 195, 74, 0.15) !important;

    /* 첫 번째 td에 왼쪽 테두리 추가 */
    td:first-child {
      border-left: 3px solid ${COLOR_SYSTEM.get("Green")?.[500]} !important;
      padding-left: 2px;
    }
  }

  /* [2025-01-24] 추가: 의약품명 클릭 가능 스타일 */
  .medication-name {
    cursor: pointer;
    color: ${COLOR_SYSTEM.get("Skyblue")?.[600]};
    font-weight: 500;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: ${COLOR_SYSTEM.get("Skyblue")?.[700]};
      text-decoration: underline;
    }
  }
`;

const DrugImageCell = styled.td`
  text-align: center;
  padding: 0 !important;
  vertical-align: middle;
  line-height: 0;
  font-size: 0;
`;

interface PrescriptionTableDisplayProps {
  prescriptionGroups?: any[];
  otc?: any[];
  supplements?: any[];
  onMedicationClick?: (medication: any) => void;
  viewMode?: "date" | "hospital";
  highlightedItemCode?: string | null;
  // 병원 하이라이트(spec 설계 결정 7) — alias(대문자). 매칭 그룹 전부 강조(파랑 계열).
  highlightedHospital?: string | null;
  // 처방전 하이라이트(spec 설계 결정 11) — groupId. 그 그룹 하나만 강조(같은 파랑).
  highlightedGroupId?: string | null;
  showDrugImages?: boolean;
  drugsData?: Drug[];
  /** "처방의약품" 제목 줄 표시 여부. 기본 true(기존 화면 유지). */
  showPrescriptionTitle?: boolean;
  /**
   * "처방의약품" 제목 바로 아래 설명문(인쇄 뷰의 복용약 범위 안내). 기본 없음.
   * 처방전이 0건이어도 이 문구가 있으면 제목+문구는 렌더한다 — "없습니다" 설명이 지면에 남아야 하므로.
   */
  prescriptionCaption?: string;
  /**
   * 제목 줄의 출처 범례 안내 아이콘(ⓘ) 표시 여부. 기본 true.
   * 인쇄 뷰는 hover 툴팁이 지면에서 무의미해 아이콘만 끄고 제목은 남긴다
   * (일반의약품·건강기능식품 라벨과의 3구분 위계 유지).
   */
  showSourceLegend?: boolean;
  /**
   * "검토 포함" 행 배지 대상 itemCode 목록 — 보고서에서 다룬 약을 해당 약 행에만 표시한다.
   * markEndedPrescriptions('종료 예상' 표시 토글)와 연동 — 토글이 꺼지면 함께 숨는다.
   * 기본 없음(기존 화면 무변화).
   */
  mentionedItemCodes?: string[];
  /**
   * 행 단위 "종료 예상" 배지 — 처방일+투약일수 기준으로 복용기간이 지난 처방약에 붙는다.
   * 일반·병합 처방전 모두 해당. 기본 false(기존 화면 무변화). 인쇄 뷰의 복용중 범위에서 켠다.
   */
  markEndedPrescriptions?: boolean;
  /**
   * 약물명 아래 분류 표기 — 'meft' = 약효분류번호+분류명, 'atc' = ATC 코드+대분류명.
   * 결측 행은 생략. 기본 'none'(기존 화면 무변화). 인쇄 뷰에서만 켠다.
   */
  classificationDisplay?: 'none' | 'meft' | 'atc';
  /**
   * 출처 아이콘(수기/OCR/NHIS 등) 표시 여부. 기본 true(기존 화면 유지).
   * 인쇄 뷰는 hover 툴팁이 지면에서 무의미하고 시각 노이즈라 끈다.
   */
  showSourceBadges?: boolean;
}

// 병원 그룹 강조 스타일 — 파랑 계열(약물 하이라이트 초록과 형태 통일, 색만 분리 · spec 설계 결정 7).
// 약물 하이라이트(tbody tr.highlighted)와 형태를 맞춘다: 각진 모서리(radius 없음) + 좌측 바 3px.
// 색(Blue 400)은 두 하이라이트를 가르는 축이라 통일 대상이 아니다. paddingLeft는 11px로 1px 보정 —
// 바가 4px→3px로 얇아진 만큼(3+11=14px) 강조 시 본문 좌측 위치를 이전(4+10=14px)과 동일하게 유지한다.
// 행 단위 "검토 포함" 배지 — 보고서에서 다룬 약임을 해당 약 행에만 표시한다 ("종료 예상" 옆).
// 처방전 제목 줄에 붙이면 언급 안 된 동봉약까지 언급된 것처럼 읽혀 과잉 주장 (지니님 확정, 2026-09-05)
// 인쇄 뷰의 배지 범례가 실물 미리보기로 재사용하므로 export.
export const ReviewIncludedBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Orange")?.[700]};
  background: ${COLOR_SYSTEM.get("Orange")?.[10]};
  border-radius: 4px;
  padding: 1px 6px;
  margin-left: 6px;
  white-space: nowrap;

  @media print {
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }
`;

// 행 단위 "종료 예상" 배지 — 처방일+투약일수 기준으로 복용기간이 지난 약에 붙는다 (약물명 옆).
// 붉은색 딱지(지니님 확정, 2026-09-05). 패널의 옵션 라벨이 실물 미리보기로 재사용하므로 export.
export const EndedDrugBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Red")?.[600]};
  background: ${COLOR_SYSTEM.get("Red")?.[10]};
  border-radius: 4px;
  padding: 1px 6px;
  margin-left: 6px;
  white-space: nowrap;

  @media print {
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }
`;

/**
 * 약물명 셀 고정 3단 구조 (지니님 확정, 2026-09-05): ①약품명 ②분류 ③배지(종료 예상·검토 포함)
 * — 각 줄 무조건 분리. 배지를 이름 뒤 인라인으로 두면 긴 약물명 줄바꿈 중간에 끼어 어지럽다
 */
const ClassificationLine = styled.div`
  font-size: 11px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin-top: 3px;
`;

const BadgeLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 3px;

  ${EndedDrugBadge}, ${ReviewIncludedBadge} {
    margin-left: 0;
  }
`;

const hospitalHighlightStyle = (on: boolean): React.CSSProperties | undefined =>
  on
    ? {
        backgroundColor: "rgba(33, 150, 243, 0.12)",
        borderLeft: `3px solid ${COLOR_SYSTEM.get("Blue")?.[400]}`,
        paddingLeft: "11px",
        transition: "background-color 0.2s ease",
      }
    : undefined;

const PrescriptionTableDisplay: React.FC<PrescriptionTableDisplayProps> = ({
  prescriptionGroups = [],
  otc = [],
  supplements = [],
  onMedicationClick,
  viewMode = "date",
  highlightedItemCode,
  highlightedHospital,
  highlightedGroupId,
  showDrugImages = false,
  drugsData,
  showPrescriptionTitle = true,
  showSourceLegend = true,
  prescriptionCaption,
  mentionedItemCodes,
  markEndedPrescriptions = false,
  classificationDisplay = 'none',
  showSourceBadges = true,
}) => {
  // 분류 표기 문자열 — 코드 결측이면 null(줄 생략). 라벨 결측은 코드만이라도 표기
  const classificationText = (rx: Prescription): string | null => {
    if (classificationDisplay === 'meft' && rx.meftDivNo) {
      return `${rx.meftDivNo} ${meftDivName(rx.meftDivNo)}`.trim();
    }
    if (classificationDisplay === 'atc' && rx.atcCode) {
      // ATC는 3단계(약리학적 소그룹, 앞 4글자)까지만 표기 — 5단계 전체 코드는 지면에 과함 (지니님 확정)
      return `${rx.atcCode.slice(0, 4)} ${atcMainName(rx.atcCode)}`.trim();
    }
    return null;
  };
  // 행 단위 종료 판정 — 렌더 시점 기준(일 단위 판정이라 렌더 중 시각 오차는 무의미)
  const now = new Date();
  const isEndedRow = (rx: Prescription) => markEndedPrescriptions && !isOngoingOn(rx, now);
  // "검토 포함"도 '종료 예상' 표시 토글(markEndedPrescriptions)에 연동 (지니님 확정)
  const isReviewIncludedRow = (rx: Prescription) =>
    markEndedPrescriptions && !!rx.itemCode && !!mentionedItemCodes?.includes(rx.itemCode);
  const handleMedicationClick = (medication: any) => {
    if (!onMedicationClick) return;
    onMedicationClick(medication);
  };

  // 병원 매칭: alias(대문자 정규화) — 표기 흔들려도 정확. 매칭 그룹 전부 강조.
  const normHighlight = (highlightedHospital || "").toUpperCase();
  const isHospitalOn = (alias?: string | null) =>
    !!normHighlight && (alias || "").toUpperCase() === normHighlight;
  // 처방전 매칭: groupId 정체성(위치 인덱스 아님). 그 그룹 하나만 강조.
  const isGroupIdOn = (groupId?: string | null) =>
    !!highlightedGroupId && groupId === highlightedGroupId;
  // 개별 그룹(날짜별 보기·병원별 내부) 강조 여부 — 병원 축 또는 처방전 축.
  const isGroupOn = (group: any) => isHospitalOn(group?.hospitalAlias) || isGroupIdOn(group?.groupId);

  // 병원별 보기 렌더링
  const renderHospitalView = () => {
    const hospitalGroups = groupPrescriptionsByHospital(prescriptionGroups);

    return (
      <div>
        {showPrescriptionTitle && (
          <TableTitle>
            처방의약품
            {showSourceLegend && (
              <InfoIconButton
                tooltip={<SourceLegend />}
                iconName="Information Circle"
                iconSize={14}
                iconColor={COLOR_SYSTEM.get("Gray")?.[400]}
                tooltipPosition="bottom"
                tooltipMaxWidth="160px"
                style={{ marginLeft: "4px", display: "inline-flex", verticalAlign: "middle" }}
              />
            )}
          </TableTitle>
        )}
        {prescriptionCaption && <PrescriptionCaption>{prescriptionCaption}</PrescriptionCaption>}
        {hospitalGroups.map((hospitalGroup) => {
          const groupAlias = hospitalGroup.prescriptionGroups[0]?.hospitalAlias;
          return (
          <HospitalGroupContainer
            key={hospitalGroup.hospital}
            data-hospital-alias={groupAlias || undefined}
            style={hospitalHighlightStyle(isHospitalOn(groupAlias))}
          >
            <HospitalTitle>
              {hospitalGroup.hospital || "병원명 없음"}
              {hospitalGroup.prescriptionGroups[0]?.hospitalAlias && (
                <span style={{ fontWeight: 600 }}>
                  ({hospitalGroup.prescriptionGroups[0].hospitalAlias}병원)
                </span>
              )}
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "normal",
                  color: COLOR_SYSTEM.get("Gray")?.[500],
                }}
              >
                ({hospitalGroup.prescriptionGroups.length}개 처방전)
              </span>
            </HospitalTitle>
            {hospitalGroup.prescriptionGroups.map((group, groupIndex) => (
              <div
                key={group.groupId}
                data-group-id={group.groupId || undefined}
                style={{
                  marginBottom: "16px",
                  paddingLeft: "16px",
                  ...hospitalHighlightStyle(isGroupIdOn(group.groupId)),
                }}
              >
                <div style={{ display: "flex", alignItems: "baseline", marginBottom: 4 }}>
                  <span style={{ fontSize: "14px", fontWeight: 500, marginRight: 8 }}>처방전 {groupIndex + 1}</span>
                  <span style={{ fontSize: "12px", color: COLOR_SYSTEM.get("Gray")?.[500] }}>
                    처방일자:{" "}
                    {group.prescriptionDate
                      ? typeof group.prescriptionDate === "string"
                        ? new Date(group.prescriptionDate).toLocaleDateString("ko-KR")
                        : (group.prescriptionDate as Date).toLocaleDateString("ko-KR")
                      : "-"}
                  </span>
                </div>
                <PrescriptionTableWrap>
                  <PrescriptionTable $clickable={!!onMedicationClick}>
                    <thead>
                      <tr>
                        <th style={{ width: "25px", padding: "4px 2px", textAlign: "center" }}></th>
                        <th style={{ width: showDrugImages ? "30%" : "40%" }}>약물명</th>
                        <th style={{ width: "10%" }}>용량</th>
                        <th style={{ width: "10%" }}>횟수</th>
                        <th style={{ width: "10%" }}>기간</th>
                        <th style={{ width: showDrugImages ? "15%" : "25%" }}>지시사항</th>
                        {showDrugImages && (
                          <th style={{ width: "120px", textAlign: "center" }}>의약품 이미지</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {group.prescriptions?.map((prescription: any, index: number) => (
                        <tr
                          key={index}
                          className={
                            highlightedItemCode && prescription.itemCode === highlightedItemCode
                              ? "highlighted"
                              : ""
                          }
                        >
                          <td
                            style={{
                              textAlign: "center",
                              color: COLOR_SYSTEM.get("Gray")?.[500],
                              fontSize: "11px",
                              padding: "4px 2px",
                              fontWeight: 500,
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            className={onMedicationClick ? "medication-name" : ""}
                            onClick={() => onMedicationClick && handleMedicationClick(prescription)}
                          >
                            {prescription.medicationName || "-"}
                            {showSourceBadges && <SourceBadge source={prescription.source} />}
                            {classificationText(prescription) && (
                              <ClassificationLine>{classificationText(prescription)}</ClassificationLine>
                            )}
                            {(isEndedRow(prescription) || isReviewIncludedRow(prescription)) && (
                              <BadgeLine>
                                {isEndedRow(prescription) && <EndedDrugBadge>종료 예상</EndedDrugBadge>}
                                {isReviewIncludedRow(prescription) && (
                                  <ReviewIncludedBadge>검토 포함</ReviewIncludedBadge>
                                )}
                              </BadgeLine>
                            )}
                          </td>
                          <td>
                            {prescription.dosagePerAdministration && prescription.dosageUnit
                              ? `${prescription.dosagePerAdministration}${prescription.dosageUnit}`
                              : prescription.dosagePerAdministration ||
                                prescription.dosageUnit ||
                                "-"}
                          </td>
                          <td>
                            {prescription.timesPerDay ? `${prescription.timesPerDay}회` : "-"}
                          </td>
                          <td>
                            {prescription.daysOfAdministration
                              ? `${prescription.daysOfAdministration}일`
                              : "-"}
                          </td>
                          <td>{prescription.usageInstruction || "-"}</td>
                          {showDrugImages && (
                            <DrugImageCell>
                              {prescription.itemCode && (
                                <Image
                                  src={getDrugImageUrl(prescription.itemCode)}
                                  alt="의약품"
                                  width={120}
                                  objectFit="contain"
                                  enableHoverZoom={false}
                                  showOnError={false}
                                />
                              )}
                            </DrugImageCell>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </PrescriptionTable>
                </PrescriptionTableWrap>
              </div>
            ))}
          </HospitalGroupContainer>
          );
        })}
      </div>
    );
  };

  // 날짜별 보기 렌더링 (기존 로직)
  const renderDateView = () => {
    return (
      <div>
        {showPrescriptionTitle && (
          <TableTitle>
            처방의약품
            {showSourceLegend && (
              <InfoIconButton
                tooltip={<SourceLegend />}
                iconName="Information Circle"
                iconSize={14}
                iconColor={COLOR_SYSTEM.get("Gray")?.[400]}
                tooltipPosition="bottom"
                tooltipMaxWidth="160px"
                style={{ marginLeft: "4px", display: "inline-flex", verticalAlign: "middle" }}
              />
            )}
          </TableTitle>
        )}
        {prescriptionCaption && <PrescriptionCaption>{prescriptionCaption}</PrescriptionCaption>}
        {prescriptionGroups.map((group, groupIndex) => (
          <div
            key={group.groupId}
            data-hospital-alias={group.hospitalAlias || undefined}
            data-group-id={group.groupId || undefined}
            style={hospitalHighlightStyle(isGroupOn(group))}
          >
            <div style={{ display: "flex", alignItems: "baseline", marginBottom: 4 }}>
              <span style={{ fontSize: "14px", fontWeight: 500, marginRight: 8 }}>처방전 {groupIndex + 1}</span>
              <span style={{ fontSize: "12px", color: COLOR_SYSTEM.get("Gray")?.[500] }}>
                처방일자:{" "}
                {group.prescriptionDate
                  ? typeof group.prescriptionDate === "string"
                    ? new Date(group.prescriptionDate).toLocaleDateString("ko-KR")
                    : (group.prescriptionDate as Date).toLocaleDateString("ko-KR")
                  : "-"}
                {group.hospital && group.hospital !== "null" &&
                    `　　병원: ${group.hospital}${group.hospitalAlias ? `(${group.hospitalAlias}병원)` : ""}`}
              </span>
            </div>
            <PrescriptionTableWrap>
              <PrescriptionTable $clickable={!!onMedicationClick}>
                <thead>
                  <tr>
                    <th style={{ width: "25px", padding: "4px 2px", textAlign: "center" }}></th>
                    <th style={{ width: showDrugImages ? "35%" : "45%" }}>약물명</th>
                    <th style={{ width: "10%" }}>용량</th>
                    <th style={{ width: "10%" }}>횟수</th>
                    <th style={{ width: "10%" }}>기간</th>
                    <th style={{ width: showDrugImages ? "20%" : "30%" }}>지시사항</th>
                    {showDrugImages && (
                      <th style={{ width: "120px", textAlign: "center" }}>의약품 이미지</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {group.prescriptions?.map((prescription: any, index: number) => (
                    <tr
                      key={index}
                      className={
                        highlightedItemCode && prescription.itemCode === highlightedItemCode
                          ? "highlighted"
                          : ""
                      }
                    >
                      <td
                        style={{
                          textAlign: "center",
                          color: COLOR_SYSTEM.get("Gray")?.[500],
                          fontSize: "11px",
                          padding: "4px 2px",
                          fontWeight: 500,
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        className={onMedicationClick ? "medication-name" : ""}
                        onClick={() => onMedicationClick && handleMedicationClick(prescription)}
                      >
                        {prescription.medicationName || "-"}
                        {showSourceBadges && <SourceBadge source={prescription.source} />}
                        {classificationText(prescription) && (
                          <ClassificationLine>{classificationText(prescription)}</ClassificationLine>
                        )}
                        {(isEndedRow(prescription) || isReviewIncludedRow(prescription)) && (
                          <BadgeLine>
                            {isEndedRow(prescription) && <EndedDrugBadge>종료 예상</EndedDrugBadge>}
                            {isReviewIncludedRow(prescription) && (
                              <ReviewIncludedBadge>검토 포함</ReviewIncludedBadge>
                            )}
                          </BadgeLine>
                        )}
                      </td>
                      <td>
                        {prescription.dosagePerAdministration && prescription.dosageUnit
                          ? `${prescription.dosagePerAdministration}${prescription.dosageUnit}`
                          : prescription.dosagePerAdministration || prescription.dosageUnit || "-"}
                      </td>
                      <td>{prescription.timesPerDay ? `${prescription.timesPerDay}회` : "-"}</td>
                      <td>
                        {prescription.daysOfAdministration
                          ? `${prescription.daysOfAdministration}일`
                          : "-"}
                      </td>
                      <td>{prescription.usageInstruction || "-"}</td>
                      {showDrugImages && (
                        <DrugImageCell>
                          {prescription.itemCode && (
                            <Image
                              src={getDrugImageUrl(prescription.itemCode)}
                              alt="의약품"
                              width={120}
                              objectFit="contain"
                              enableHoverZoom={false}
                              showOnError={false}
                            />
                          )}
                        </DrugImageCell>
                      )}
                    </tr>
                  ))}
                </tbody>
              </PrescriptionTable>
            </PrescriptionTableWrap>
          </div>
        ))}
      </div>
    );
  };

  return (
    <PrescriptionsContainer>
      {(prescriptionGroups.length > 0 || !!prescriptionCaption) &&
        (viewMode === "hospital" ? renderHospitalView() : renderDateView())}

      {otc.length > 0 && (
        <div>
          <TableTitle>일반의약품</TableTitle>
          <PrescriptionTableWrap>
            <PrescriptionTable $clickable={!!onMedicationClick}>
              <thead>
                <tr>
                  <th style={{ width: "25px", padding: "4px 2px", textAlign: "center" }}></th>
                  <th style={{ width: showDrugImages ? "30%" : "40%" }}>약물명</th>
                  <th style={{ width: showDrugImages ? "45%" : "55%" }}>약사코멘트</th>
                  {showDrugImages && (
                    <th style={{ width: "120px", textAlign: "center" }}>의약품 이미지</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {otc.map((otcItem: any, index: number) => (
                  <tr
                    key={index}
                    className={
                      highlightedItemCode && otcItem.itemCode === highlightedItemCode
                        ? "highlighted"
                        : ""
                    }
                  >
                    <td
                      style={{
                        textAlign: "center",
                        color: COLOR_SYSTEM.get("Gray")?.[500],
                        fontSize: "11px",
                        padding: "4px 2px",
                        fontWeight: 500,
                      }}
                    >
                      {index + 1}
                    </td>
                    <td
                      className={onMedicationClick ? "medication-name" : ""}
                      onClick={() => onMedicationClick && handleMedicationClick(otcItem)}
                    >
                      {otcItem.medicationName || otcItem.name}
                      {showSourceBadges && <SourceBadge source={otcItem.source} />}
                    </td>
                    <td>{otcItem.notes || otcItem.comment || "-"}</td>
                    {showDrugImages && (
                      <DrugImageCell>
                        {otcItem.itemCode && (
                          <Image
                            src={getDrugImageUrl(otcItem.itemCode)}
                            alt="의약품"
                            width={120}
                            objectFit="contain"
                            enableHoverZoom={false}
                            showOnError={false}
                          />
                        )}
                      </DrugImageCell>
                    )}
                  </tr>
                ))}
              </tbody>
            </PrescriptionTable>
          </PrescriptionTableWrap>
        </div>
      )}

      {supplements.length > 0 && (
        <div>
          <TableTitle>건강기능식품</TableTitle>
          <PrescriptionTableWrap>
            <PrescriptionTable $clickable={!!onMedicationClick}>
              <thead>
                <tr>
                  <th style={{ width: "25px", padding: "4px 2px", textAlign: "center" }}></th>
                  <th style={{ width: "40%" }}>제품명</th>
                  <th style={{ width: "55%" }}>약사코멘트</th>
                </tr>
              </thead>
              <tbody>
                {supplements.map((supplement: any, index: number) => (
                  <tr
                    key={index}
                    className={
                      highlightedItemCode && supplement.itemCode === highlightedItemCode
                        ? "highlighted"
                        : ""
                    }
                  >
                    <td
                      style={{
                        textAlign: "center",
                        color: COLOR_SYSTEM.get("Gray")?.[500],
                        fontSize: "11px",
                        padding: "4px 2px",
                        fontWeight: 500,
                      }}
                    >
                      {index + 1}
                    </td>
                    <td
                      className={onMedicationClick && supplement.itemCode ? "medication-name" : ""}
                      onClick={() =>
                        onMedicationClick && supplement.itemCode && handleMedicationClick(supplement)
                      }
                    >
                      {supplement.medicationName || supplement.name}
                      {showSourceBadges && <SourceBadge source={supplement.source} />}
                    </td>
                    <td>{supplement.notes || supplement.comment || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </PrescriptionTable>
          </PrescriptionTableWrap>
        </div>
      )}
    </PrescriptionsContainer>
  );
};

export default PrescriptionTableDisplay;
